/*
 * Confirmation for the collection cards' add to cart button.
 *
 * Two paths, depending on the store's cart type:
 *
 * - With a cart drawer or notification on the page, Dawn's product-form publishes
 *   cartUpdate when the request lands (assets/product-form.js) and that is the signal.
 *   The drawer is the confirmation, so the state is shown and then reverted in place.
 * - Without one — cart_type "page" — that same handler leaves for
 *   /cart before publishing anything, so the click gets no acknowledgement at all. The
 *   submit is taken over here: the item is added over the Cart AJAX API, the confirmed
 *   state is shown, and only then does the shopper go on to /cart, which is where the
 *   click would have landed anyway. The pause is there so the confirmation is seen on the
 *   way past. The loading classes Dawn would have set are applied here too, since the CSS
 *   leans on them for locking the other buttons.
 *
 * The chips re-render the grid, so buttons are resolved per event rather than bound once.
 */
(() => {
  /* The script tag is part of the section, so a theme-editor section reload re-runs it.
     Without this a second document-capture listener would be registered and one click
     would add twice. Dawn guards its own scripts the same way, via customElements.get. */
  if (window.collectionAddToCartLoaded) return;
  window.collectionAddToCartLoaded = true;

  const ADDED_CLASS = 'quick-add__submit--added';
  const ADDED_DURATION = 2000;
  /* Long enough to read the confirmation, short enough that the cart still feels like a
     response to the click rather than a separate wait. */
  const CONFIRMED_PAUSE = 900;
  /* Matches collection-category-pills.js. A stalled add would otherwise hold .loading
     forever, and the CSS keys its grid-wide lock on that class, so every other button on
     the page would stay inert with nothing shown and no way back but a reload. */
  const REQUEST_TIMEOUT = 15000;
  const revertTimers = new WeakMap();
  /* Paired with revertTimers: whichever of the two fires first cancels the other, so a
     button is never left mid-state and never reverted twice. */
  const drawerOpenWatchers = new WeakMap();
  /* One add at a time across the whole grid. The CSS lock is presentation only: it cannot
     stop a keyboard activation, and a chip repaint hands back fresh buttons with no
     .loading, which lifts it for the pointer too. */
  let addInFlight = false;
  let navigationTimer = null;

  const getGrid = () => document.getElementById('product-grid');

  const clearPendingRevert = (button) => {
    const timer = revertTimers.get(button);
    if (timer) {
      clearTimeout(timer);
      revertTimers.delete(button);
    }
    const watcher = drawerOpenWatchers.get(button);
    if (watcher) {
      watcher.disconnect();
      drawerOpenWatchers.delete(button);
    }
  };

  const revertAdded = (button) => {
    clearPendingRevert(button);
    button.classList.remove(ADDED_CLASS);
  };

  const showAdded = (button, { revert }) => {
    if (!button) return;
    clearPendingRevert(button);
    button.classList.add(ADDED_CLASS);
    if (!revert) return;
    revertTimers.set(button, setTimeout(() => revertAdded(button), ADDED_DURATION));
  };

  if (document.querySelector('cart-notification, cart-drawer')) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    const cartDrawer = document.querySelector('cart-drawer');

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'product-form') return;
      const variantId = String(event.productVariantId ?? '');
      const variantInput = getGrid()?.querySelector(`.product-variant-id[value="${variantId}"]`);
      const button = variantInput?.closest('form')?.querySelector('.quick-add__submit');
      if (!button) return;

      /* The timer is the fallback, not the plan: with no drawer to hand over to — a cart
         notification, or a drawer whose open never lands — the button still has to come
         back on its own rather than sit confirmed for good. */
      showAdded(button, { revert: true });
      if (!cartDrawer || cartDrawer.classList.contains('active')) return;

      /* Once the panel is up it is the confirmation, so the button steps back to its
         default as it opens. On its own timer it instead reverted a second or two later,
         under a drawer that was still covering the page — and if the shopper closed the
         drawer before that, the button changed under them for no reason they could see. */
      const watcher = new MutationObserver(() => {
        if (!cartDrawer.classList.contains('active')) return;
        revertAdded(button);
      });
      watcher.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      drawerOpenWatchers.set(button, watcher);
    });
    return;
  }

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!form.matches('#product-grid form[data-type="add-to-cart-form"]')) return;

      /* The card form is not the only match. A product with several variants or quantity
         rules renders <quick-add-modal> inside the grid, and quick-add.js fills it with the
         product page's own section — a {% form 'product' %} wrapped in its own
         <product-form>, carrying the same data-type, whose submit is .product-form__submit.
         Tightening the selector cannot separate the two. The button is what does: without
         it there is nothing to confirm on and nothing to lock, so the modal is left to
         Dawn, which shows quantity and inventory errors inline where the shopper is
         looking. Taking it over here would drop them on /cart with no explanation. */
      const button = form.querySelector('.quick-add__submit');
      if (!button) return;

      /* Capture phase, so product-form's own handler — which would leave the page for
         /cart before anything can be confirmed — never runs. */
      event.preventDefault();
      event.stopImmediatePropagation();

      if (addInFlight || button.getAttribute('aria-disabled') === 'true') return;
      addInFlight = true;

      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
          signal: AbortSignal.timeout(REQUEST_TIMEOUT),
        });
        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
        /* No revert, and the loading classes are left on: the page is leaving, and the
           button going clickable again for a moment first would invite a second add. */
        showAdded(button, { revert: false });
        navigationTimer = setTimeout(() => {
          navigationTimer = null;
          window.location = window.routes.cart_url;
        }, CONFIRMED_PAUSE);
      } catch (error) {
        /* Never leave the button claiming success. The cart page is where this click was
           always headed, so it is the honest place to land. */
        console.warn('[collection-add-to-cart]', error);
        addInFlight = false;
        window.location = window.routes.cart_url;
      }
    },
    true,
  );

  /* The pause to /cart must not outlive the page that started it. The grid's CSS lock only
     covers the other add buttons, so a card's image or title stays clickable throughout:
     leaving the timer to run means a shopper who taps a product lands on /cart instead. If
     the product page wins the race the collection page is frozen into the bfcache with the
     timer still pending, and Back would resume it straight into a second trip to /cart —
     with no way of returning to the grid at all. */
  const cancelNavigation = () => {
    if (navigationTimer === null) return;
    clearTimeout(navigationTimer);
    navigationTimer = null;
  };

  window.addEventListener('pagehide', cancelNavigation);

  /* Coming back from the cart restores this page from the bfcache exactly as it left,
     which would be with the clicked button still confirmed and still locked. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    cancelNavigation();
    addInFlight = false;
    getGrid()
      ?.querySelectorAll(`.${ADDED_CLASS}, .quick-add__submit.loading`)
      .forEach((button) => {
        button.classList.remove(ADDED_CLASS, 'loading');
        button.removeAttribute('aria-disabled');
      });
  });
})();
